import { distributionsInMonth, parsePayPeriodParams } from './cashPolicy';
import { sCurveValue } from './scurve';
import type { DriverParams, DriverType, RecipeDefaults, SchemaEntry, View } from './types';

export interface DriverContext {
    getNumeric: (view: View, lineKey: string, t: number) => number | null;
    daysInMonth: (t: number) => number;
    monthKey: (t: number) => string;
    schemaByKey: Record<string, SchemaEntry>;
    driverInput: (view: View, lineKey: string, t: number) => number | null;
    driverParams: (view: View, lineKey: string) => DriverParams | undefined;
    addDiagnostic: (code: string, message: string, view?: View, lineKey?: string, t?: number) => void;
    view: View;
    recipeDefaults?: RecipeDefaults;
    cashPolicyAnchor?: string | null;
}

const HOURLY_FTE_BOH = 'is.hourly_fte_boh';
const HOURLY_FTE_FOH = 'is.hourly_fte_foh';
const AVG_RATE_BOH = 'is.avg_hourly_rate_boh';
const AVG_RATE_FOH = 'is.avg_hourly_rate_foh';
const SALARIED_HC = 'is.salaried_headcount';
const AVG_SALARY = 'is.avg_salary_annual';

function resolvePayPeriodParams(
    ctx: DriverContext,
    view: View,
    lineKey: string,
): ReturnType<typeof parsePayPeriodParams> {
    const params = ctx.driverParams(view, lineKey) ?? {};
    const cadence =
        params.cadence ?? ctx.recipeDefaults?.default_pay_cadence ?? 'biweekly';
    const anchor =
        params.anchor_date ??
        ctx.recipeDefaults?.default_pay_anchor_date ??
        ctx.cashPolicyAnchor ??
        undefined;
    if (!anchor) {
        return null;
    }
    return parsePayPeriodParams({ cadence, anchor_date: anchor });
}

function payDatesInMonth(
    ctx: DriverContext,
    view: View,
    lineKey: string,
    t: number,
): number | null {
    const params = resolvePayPeriodParams(ctx, view, lineKey);
    if (!params) {
        return null;
    }
    return distributionsInMonth(params.cadence, params.anchor_date, ctx.monthKey(t));
}

function roundUsd(value: number): number {
    return Math.round(value * 100) / 100;
}

const GPS_KEY = 'is.gross_product_sales';
const ORDERS_KEY = 'is.total_orders';
const TICKET_KEY = 'is.avg_ticket';
const SQFT_KEY = 'is.square_footage';
const DAYS_OPEN_KEY = 'is.days_open_for_business';
const BOH_KEY = 'is.boh';
const FOH_KEY = 'is.foh';
const SALARIED_HC_KEY = 'is.salaried_headcount';

function fteDenominator(ctx: DriverContext, view: View, t: number): number | null {
    const boh = ctx.getNumeric(view, BOH_KEY, t) ?? 0;
    const foh = ctx.getNumeric(view, FOH_KEY, t) ?? 0;
    const hourly = boh + foh;
    if (hourly > 0) {
        return hourly;
    }
    if (view === 'ga') {
        const salaried = ctx.getNumeric(view, SALARIED_HC_KEY, t);
        return salaried != null && salaried > 0 ? salaried : null;
    }
    return null;
}
const TOTAL_COGS_KEY = 'is.total_cogs';
const NET_REVENUE_KEY = 'is.net_revenue';

function nullIfMissing(value: number | null | undefined): number | null {
    if (value == null || !Number.isFinite(value)) {
        return null;
    }
    return value;
}

function applyPctOfLine(
    ctx: DriverContext,
    view: View,
    lineKey: string,
    t: number,
    d: number | null,
): number | null {
    if (d == null) {
        return null;
    }
    const ref = ctx.driverParams(view, lineKey)?.ref;
    if (!ref) {
        return null;
    }
    const refValue = ctx.getNumeric(view, ref, t);
    if (refValue == null) {
        return null;
    }
    return d * refValue;
}

export function impliedDriverInput(
    driver: DriverType,
    ctx: DriverContext,
    view: View,
    lineKey: string,
    t: number,
): number | null {
    const v = nullIfMissing(ctx.getNumeric(view, lineKey, t));
    if (v == null) {
        return null;
    }

    switch (driver) {
        case 'count_value':
        case 'dollar_value':
            return v;
        case 'count_per_calendar_day':
        case 'dollar_per_calendar_day': {
            const days = ctx.daysInMonth(t);
            if (days === 0) {
                return null;
            }
            return v / days;
        }
        case 'dollar_per_pay_period': {
            const payDates = payDatesInMonth(ctx, view, lineKey, t);
            if (payDates === null || payDates === 0) {
                return null;
            }
            return v / payDates;
        }
        case 'count_per_open_day':
        case 'dollar_per_open_day': {
            const daysOpen = ctx.getNumeric(view, DAYS_OPEN_KEY, t);
            if (daysOpen == null || daysOpen === 0) {
                return null;
            }
            return v / daysOpen;
        }
        case 'dollar_per_sq_ft': {
            const sqft = ctx.getNumeric(view, SQFT_KEY, t);
            if (sqft == null || sqft === 0) {
                return null;
            }
            return v / sqft;
        }
        case 'dollar_per_1000_sqft_calendar_day': {
            const sqft = ctx.getNumeric(view, SQFT_KEY, t);
            const days = ctx.daysInMonth(t);
            if (sqft == null || sqft === 0 || days === 0) {
                return null;
            }
            return (v * 1000) / (sqft * days);
        }
        case 'dollar_per_fte': {
            const fte = fteDenominator(ctx, view, t);
            if (fte == null || fte === 0) {
                return null;
            }
            return v / fte;
        }
        case 'mom_pct': {
            const prev = ctx.getNumeric(view, lineKey, t - 1);
            if (prev == null || prev === 0) {
                return null;
            }
            return v / prev - 1;
        }
        case 'mom_dollar': {
            const prev = ctx.getNumeric(view, lineKey, t - 1);
            if (prev == null) {
                return null;
            }
            return v - prev;
        }
        case 'yoy_pct': {
            const prior = ctx.getNumeric(view, lineKey, t - 12);
            if (prior == null || prior === 0) {
                return null;
            }
            return v / prior - 1;
        }
        case 'yoy_dollar': {
            const prior = ctx.getNumeric(view, lineKey, t - 12);
            if (prior == null) {
                return null;
            }
            return v - prior;
        }
        case 'pct_gross_sales': {
            const gps = ctx.getNumeric(view, GPS_KEY, t);
            if (gps == null || gps === 0) {
                return null;
            }
            return v / gps;
        }
        case 'pct_gross_sales_growth': {
            const prev = ctx.getNumeric(view, lineKey, t - 1);
            const gps = ctx.getNumeric(view, GPS_KEY, t);
            const gpsPrev = ctx.getNumeric(view, GPS_KEY, t - 1);
            if (prev == null || prev === 0 || gps == null || gpsPrev == null || gpsPrev === 0) {
                return null;
            }
            const growth = gps / gpsPrev - 1;
            if (growth === 0) {
                return null;
            }
            return (v / prev - 1) / growth;
        }
        case 'pct_of_line':
        case 'dollar_per_line_unit': {
            const ref = ctx.driverParams(view, lineKey)?.ref;
            if (!ref) {
                return null;
            }
            const refValue = ctx.getNumeric(view, ref, t);
            if (refValue == null || refValue === 0) {
                return null;
            }
            return v / refValue;
        }
        case 'dio':
        case 'dpo': {
            const cogs = ctx.getNumeric('consolidated', TOTAL_COGS_KEY, t);
            const days = ctx.daysInMonth(t);
            if (cogs == null || cogs === 0 || days === 0) {
                return null;
            }
            return (v * days) / cogs;
        }
        case 'dso': {
            const nr = ctx.getNumeric('consolidated', NET_REVENUE_KEY, t);
            const days = ctx.daysInMonth(t);
            if (nr == null || nr === 0 || days === 0) {
                return null;
            }
            return (v * days) / nr;
        }
        case 's_curve':
        case 'labor_buildup':
        case 'carry_forward':
        case 'ignore':
        case 'orders_x_ticket':
        default:
            return null;
    }
}

function laborComponent(
    ctx: DriverContext,
    view: View,
    lineKey: string,
    t: number,
    treatNullAsZero: boolean,
): number | null {
    const value = ctx.getNumeric(view, lineKey, t);
    if (value == null) {
        if (treatNullAsZero) {
            return 0;
        }
        ctx.addDiagnostic('driven_null', `Missing ${lineKey}`, view, lineKey, t);
        return null;
    }
    return value;
}

export function applyDriver(
    driver: DriverType,
    ctx: DriverContext,
    view: View,
    lineKey: string,
    t: number,
): number | null {
    const d = ctx.driverInput(view, lineKey, t);

    switch (driver) {
        case 'count_value':
        case 'dollar_value':
            return d;
        case 'count_per_calendar_day':
        case 'dollar_per_calendar_day':
            return d == null ? null : d * ctx.daysInMonth(t);
        case 'dollar_per_pay_period': {
            if (d == null) {
                return null;
            }
            const payDates = payDatesInMonth(ctx, view, lineKey, t);
            if (payDates === null) {
                return null;
            }
            return d * payDates;
        }
        // Per-open-day, per-sq-ft, per-FTE, and per-1000-sqft drivers yield null (rendered '-') when the denominator is 0.
        case 'count_per_open_day':
        case 'dollar_per_open_day': {
            if (d == null) {
                return null;
            }
            const daysOpen = ctx.getNumeric(view, DAYS_OPEN_KEY, t);
            if (daysOpen == null || daysOpen === 0) {
                return null;
            }
            return d * daysOpen;
        }
        case 'dollar_per_sq_ft': {
            if (d == null) {
                return null;
            }
            const sqft = ctx.getNumeric(view, SQFT_KEY, t);
            if (sqft == null || sqft === 0) {
                return null;
            }
            return d * sqft;
        }
        case 'dollar_per_1000_sqft_calendar_day': {
            if (d == null) {
                return null;
            }
            const sqft = ctx.getNumeric(view, SQFT_KEY, t);
            if (sqft == null || sqft === 0) {
                return null;
            }
            return (d * sqft * ctx.daysInMonth(t)) / 1000;
        }
        case 'dollar_per_fte': {
            if (d == null) {
                return null;
            }
            const fte = fteDenominator(ctx, view, t);
            if (fte == null || fte === 0) {
                return null;
            }
            return d * fte;
        }
        case 'dollar_per_line_unit':
            return applyPctOfLine(ctx, view, lineKey, t, d);
        case 'mom_pct': {
            if (d == null) {
                return null;
            }
            const prev = ctx.getNumeric(view, lineKey, t - 1);
            if (prev == null) {
                return null;
            }
            return prev * (1 + d);
        }
        case 'mom_dollar': {
            if (d == null) {
                return null;
            }
            const prev = ctx.getNumeric(view, lineKey, t - 1);
            if (prev == null) {
                return null;
            }
            return prev + d;
        }
        case 'yoy_pct': {
            if (d == null) {
                return null;
            }
            const prior = ctx.getNumeric(view, lineKey, t - 12);
            if (prior == null) {
                return null;
            }
            return prior * (1 + d);
        }
        case 'yoy_dollar': {
            if (d == null) {
                return null;
            }
            const prior = ctx.getNumeric(view, lineKey, t - 12);
            if (prior == null) {
                return null;
            }
            return prior + d;
        }
        case 'pct_gross_sales': {
            if (d == null) {
                return null;
            }
            const gps = ctx.getNumeric(view, GPS_KEY, t);
            if (gps == null) {
                return null;
            }
            return d * gps;
        }
        case 'pct_gross_sales_growth': {
            if (d == null) {
                return null;
            }
            const prev = ctx.getNumeric(view, lineKey, t - 1);
            const gps = ctx.getNumeric(view, GPS_KEY, t);
            const gpsPrev = ctx.getNumeric(view, GPS_KEY, t - 1);
            if (prev == null || gps == null || gpsPrev == null || gpsPrev === 0) {
                return null;
            }
            const growth = gps / gpsPrev - 1;
            return prev * (1 + d * growth);
        }
        case 'pct_of_line':
            return applyPctOfLine(ctx, view, lineKey, t, d);
        case 's_curve': {
            const params = ctx.driverParams(view, lineKey);
            if (d != null) {
                return d;
            }
            if (!params) {
                return null;
            }
            return sCurveValue(
                {
                    start: params.start ?? 0,
                    end: params.end ?? 0,
                    midpoint: params.midpoint ?? 18,
                    steepness: params.steepness ?? 1,
                },
                t,
            );
        }
        case 'dio':
        case 'dpo': {
            if (d == null) {
                return null;
            }
            const cogs = ctx.getNumeric('consolidated', TOTAL_COGS_KEY, t);
            if (cogs == null) {
                return null;
            }
            return (d / ctx.daysInMonth(t)) * cogs;
        }
        case 'dso': {
            if (d == null) {
                return null;
            }
            const nr = ctx.getNumeric('consolidated', NET_REVENUE_KEY, t);
            if (nr == null) {
                return null;
            }
            return (d / ctx.daysInMonth(t)) * nr;
        }
        case 'orders_x_ticket': {
            const orders = ctx.getNumeric(view, ORDERS_KEY, t);
            const ticket = ctx.getNumeric(view, TICKET_KEY, t);
            if (orders == null || ticket == null) {
                return null;
            }
            return orders * ticket;
        }
        case 'labor_buildup': {
            const atGa = view === 'ga';
            const fteBoh = laborComponent(ctx, view, HOURLY_FTE_BOH, t, atGa);
            const fteFoh = laborComponent(ctx, view, HOURLY_FTE_FOH, t, atGa);
            const rateBoh = laborComponent(ctx, view, AVG_RATE_BOH, t, atGa);
            const rateFoh = laborComponent(ctx, view, AVG_RATE_FOH, t, atGa);
            const headcount = laborComponent(ctx, view, SALARIED_HC, t, false);
            const salary = laborComponent(ctx, view, AVG_SALARY, t, false);
            if (
                fteBoh == null ||
                fteFoh == null ||
                rateBoh == null ||
                rateFoh == null ||
                headcount == null ||
                salary == null
            ) {
                return null;
            }
            const payDates = payDatesInMonth(ctx, view, lineKey, t);
            if (payDates == null) {
                return null;
            }
            const hourlyPart = 80 * (fteBoh * rateBoh + fteFoh * rateFoh);
            const salariedPart = headcount * salary / 26;
            const wagesGross = payDates * (hourlyPart + salariedPart);
            const netPct = ctx.driverParams(view, lineKey)?.net_of_benefits_pct ?? 0;
            return roundUsd(wagesGross * (1 - netPct));
        }
        case 'carry_forward': {
            if (ctx.schemaByKey[lineKey]?.tab === 'BS') {
                return ctx.getNumeric(view, lineKey, t - 1);
            }
            return ctx.getNumeric(view, lineKey, 0);
        }
        case 'ignore':
            return null;
        default:
            return null;
    }
}

export function applyLinked(
    ctx: DriverContext,
    view: View,
    lineKey: string,
    t: number,
    sourceKey: string,
): number {
    const prev = ctx.getNumeric(view, lineKey, t - 1) ?? 0;
    const linked = ctx.getNumeric('consolidated', sourceKey, t) ?? 0;
    if (ctx.getNumeric('consolidated', sourceKey, t) == null) {
        ctx.addDiagnostic(
            'linked_null_is',
            `Linked IS line ${sourceKey} is null; treated as 0`,
            view,
            lineKey,
            t,
        );
    }
    void view;
    void lineKey;
    return prev + linked;
}

export function applyPlug(
    totalLe: number | null,
    totalAssetsExCash: number | null,
    ctx: DriverContext,
    t: number,
): number {
    const le = totalLe ?? 0;
    const assets = totalAssetsExCash ?? 0;
    if (totalLe == null || totalAssetsExCash == null) {
        ctx.addDiagnostic('plug_null_input', 'Plug computed with null treated as 0', undefined, 'bs.cash_bank_accounts_and_petty_cash', t);
    }
    return le - assets;
}

export function getNumericOrNull(value: number | string | null | undefined): number | null {
    return nullIfMissing(typeof value === 'number' ? value : null);
}
